import type { TFunction } from 'i18next';

type ScanFailureCategory =
  | 'blockedAccess'
  | 'authenticationRequired'
  | 'processingError'
  | 'siteUnreachable'
  | 'genericFailure';

interface ScanFailureInput {
  status?: string | null;
  failureReason?: string | null;
  httpStatusCode?: number | null;
}

interface ScanFailurePresentation {
  category: ScanFailureCategory;
  title: string;
  body: string;
  summary: string;
  nextStep: string | null;
}

const BLOCKED_ACCESS_PATTERNS = [
  /\bhttp 403\b/i,
  /\bforbidden\b/i,
  /\baccess denied\b/i,
  /\bbot protection\b/i,
  /\bfirewall\b/i,
  /\bcaptcha\b/i,
  /\bcloudflare\b/i,
  /blocked the scan request/i,
] as const;

const AUTHENTICATION_REQUIRED_PATTERNS = [
  /\bhttp 401\b/i,
  /\bunauthorized\b/i,
  /\bauthentication required\b/i,
  /\brequires authentication\b/i,
  /\blogin required\b/i,
  /\bsign[- ]?in required\b/i,
] as const;

const PROCESSING_ERROR_PATTERNS = [
  /value too long for type character varying/i,
  /violates .* constraint/i,
  /duplicate key value/i,
  /invalid input syntax/i,
  /relation .* does not exist/i,
  /column .* does not exist/i,
  /internal processing error/i,
  /processing results/i,
] as const;

const SITE_UNREACHABLE_PATTERNS = [
  /could not reach the site/i,
  /could not reach any pages/i,
  /domain not found/i,
  /dns lookup failed/i,
  /connection refused/i,
  /timeout/i,
  /did not respond within the timeout period/i,
  /unreachable/i,
  /tls\/ssl certificate error/i,
] as const;

function matchesPattern(value: string | null | undefined, patterns: readonly RegExp[]): boolean {
  if (!value) return false;
  return patterns.some((pattern) => pattern.test(value));
}

function resolveCategory(input: ScanFailureInput): ScanFailureCategory {
  const reason = input.failureReason ?? null;
  if (input.status === 'auth_failed') {
    if (matchesPattern(reason, PROCESSING_ERROR_PATTERNS)) {
      return 'processingError';
    }
    if (matchesPattern(reason, SITE_UNREACHABLE_PATTERNS)) {
      return 'siteUnreachable';
    }
    return 'genericFailure';
  }

  if (input.httpStatusCode === 403 || matchesPattern(reason, BLOCKED_ACCESS_PATTERNS)) {
    return 'blockedAccess';
  }

  if (input.httpStatusCode === 401 || matchesPattern(reason, AUTHENTICATION_REQUIRED_PATTERNS)) {
    return 'authenticationRequired';
  }

  if (matchesPattern(reason, PROCESSING_ERROR_PATTERNS)) {
    return 'processingError';
  }

  if (matchesPattern(reason, SITE_UNREACHABLE_PATTERNS)) {
    return 'siteUnreachable';
  }

  return 'genericFailure';
}

function fallbackBody(
  t: TFunction,
  input: ScanFailureInput,
  category: ScanFailureCategory,
): string {
  if (input.failureReason?.trim()) {
    return input.failureReason.trim();
  }

  if (input.status === 'failed') {
    return t(`common:scanFailures.${category}.body`);
  }

  return t('common:scanFailures.genericFailure.body');
}

export function resolveScanFailurePresentation(
  t: TFunction,
  input: ScanFailureInput,
): ScanFailurePresentation {
  const category = resolveCategory(input);
  return {
    category,
    title: t(`common:scanFailures.${category}.title`),
    body: category === 'genericFailure'
      ? fallbackBody(t, input, category)
      : t(`common:scanFailures.${category}.body`),
    summary: t(`common:scanFailures.${category}.summary`),
    nextStep: category === 'genericFailure'
      ? null
      : t(`common:scanFailures.${category}.nextStep`),
  };
}

export function resolveScanFailureSummary(
  t: TFunction,
  input: ScanFailureInput,
): string | null {
  if (!input.failureReason && input.httpStatusCode == null) {
    return null;
  }

  const presentation = resolveScanFailurePresentation(t, input);
  if (presentation.category === 'genericFailure' && input.failureReason?.trim()) {
    return input.failureReason.trim();
  }

  return presentation.summary;
}
